import React, { useContext, useEffect, useRef, useState } from "react";
import { UserContext } from "../UserContext";
import Header from "../Header";
import apiClient from "../../api/client";
import { HiOutlineRefresh } from "react-icons/hi";
import { useNavigate, useSearchParams } from "react-router-dom";
import ExpiryFilterChip from "../ExpiryOverview/ExpiryFilterChip";
import ExpiryDate from "../ExpiryDate";
import PhoneCell from "../PhoneCell";
import LoadError from "../LoadError";
import { getErrorMessage } from "../../utils/network";
import { getExpiryBucket } from "../../utils/expiry";
import Pagination from "../Pagination";
import "./index.css";

/**
 * Deliberately simpler than InternetCustomerList — there's no "list all
 * customers" API on RailTel's side (only exact-username lookup), so
 * this reads from what's already stored via the link/unlink flow
 * (customer/railtel_link_account.php) instead of a live provider call.
 * That means no live status/online/package/expiry columns here — those
 * would mean one provider call per row just to render the list. Click
 * through to a customer's own RailTel tab for live details on that one
 * account.
 *
 * Also simpler on the navigation side: since this list already comes
 * from our own DB, user_id is right there in the response — no need
 * for InternetCustomerList's extra get_customer_by_mobile.php round
 * trip just to find out which internal customer a row corresponds to.
 */
const RailtelCustomersList = () => {

    const { token } = useContext(UserContext);

    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    // { message, cause } when the list couldn't be loaded — shown instead of
    // the table, so a dropped connection no longer reads "No Customers Found".
    const [loadError, setLoadError] = useState(null);
    const [search, setSearch] = useState("");

    const navigate = useNavigate();

    // ?expiry=today etc. from the dashboard. Expiry here is the last value
    // seen from RailTel (cached by railtel_sync_expiry.php / the RailTel tab).
    const [searchParams, setSearchParams] = useSearchParams();
    const expiryBucket = getExpiryBucket(searchParams.get("expiry"));
    const clearExpiryFilter = () => {
        const next = new URLSearchParams(searchParams);
        next.delete("expiry");
        setSearchParams(next, { replace: true });
    };

    // Server-side pages (railtel_customer_list.php?page=…)
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(() => {
        try { return Number(sessionStorage.getItem("rt_customers_per_page")) || 50; } catch { return 50; }
    });
    const [total, setTotal] = useState(0);
    const [hasExpiry, setHasExpiry] = useState(false);
    const [query, setQuery] = useState("");
    const reqSeq = useRef(0);

    useEffect(() => {
        const t = setTimeout(() => setQuery(search.trim()), 350);
        return () => clearTimeout(t);
    }, [search]);

    const loadCustomers = async (p = page) => {
        const my = ++reqSeq.current;
        setLoading(true);
        try {
            const { data } = await apiClient.get("/customer/railtel_customer_list.php", {
                params: { page: p, per_page: perPage, q: query || undefined, expiry: expiryBucket ? expiryBucket.key : undefined },
            });
            if (my !== reqSeq.current) return;
            if (data.status === 200) {
                setCustomers(data.results || []);
                setTotal(Number(data.total || 0));
                setHasExpiry(!!data.has_expiry);
                if (data.page && data.page !== p) setPage(data.page);
                setLoadError(null);
            } else {
                setLoadError({ message: data.message || "Unable to load customers", cause: null });
            }
        } catch (e) {
            if (my !== reqSeq.current) return;
            setLoadError({ message: getErrorMessage(e, "Unable to load customers"), cause: e });
        }
        if (my === reqSeq.current) setLoading(false);
    };

    useEffect(() => {
        setPage(1);
        loadCustomers(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, searchParams.get("expiry"), perPage]);

    const goPage = (p) => { setPage(p); loadCustomers(p); window.scrollTo({ top: 0, behavior: "smooth" }); };
    const changePerPage = (n) => {
        try { sessionStorage.setItem("rt_customers_per_page", String(n)); } catch { /* ignore */ }
        setPerPage(n);
    };

    const filteredCustomers = customers;

    const goToCustomer = (c) => {
        navigate(`/customer/${c.user_id}?tab=railtel`);
    };

    return (
        <>
            <Header />

            <div className="railtel-list-container">

                <div className="railtel-list-header">
                    <h2>RailTel Customers</h2>

                    <button className="refresh-btn" onClick={() => loadCustomers(page)}>
                        Refresh
                    </button>
                    <button className="refresh-btn-mobile" onClick={() => loadCustomers(page)}>
                        <HiOutlineRefresh />
                    </button>
                </div>

                <input
                    className="railtel-search"
                    placeholder="Search Customer, RailTel Username, Mobile, Operator..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />

                {expiryBucket && (
                    <ExpiryFilterChip
                        bucketKey={expiryBucket.key}
                        providerLabel="RailTel"
                        count={loading ? null : total}
                        onClear={clearExpiryFilter}
                    />
                )}

                {loading ? (
                    <div className="skeleton-loader">
                        {[...Array(6)].map((_, index) => (
                            <div key={index} className="skeleton-row">
                                <div className="skeleton-cell"></div>
                                <div className="skeleton-cell"></div>
                                <div className="skeleton-cell"></div>
                                <div className="skeleton-cell"></div>
                                <div className="skeleton-cell"></div>
                                <div className="skeleton-cell"></div>
                            </div>
                        ))}
                    </div>
                ) : loadError ? (

                    <LoadError error={loadError.cause} message={loadError.message} onRetry={() => loadCustomers(page)} />

                ) : (
                    <div className="operator-table-wrapper">
                        <table className="operator-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Customer</th>
                                    <th>Mobile</th>
                                    <th>RailTel Username</th>
                                    <th>Operator</th>
                                    {hasExpiry && <th>Expiry</th>}
                                    <th>Linked On</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCustomers.length === 0 && (
                                    <tr>
                                        <td colSpan={hasExpiry ? 7 : 6}>No Customers Found</td>
                                    </tr>
                                )}

                                {filteredCustomers.map((c, index) => (
                                    <tr
                                        key={c.link_id}
                                        onClick={() => goToCustomer(c)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <td>{(page - 1) * perPage + index + 1}</td>
                                        <td>{c.firstname} {c.lastname}</td>
                                        <td>
                                            <PhoneCell number={c.mobile} />
                                        </td>
                                        <td>
                                            <span
                                                className="railtel-link"
                                                onClick={(e) => { e.stopPropagation(); goToCustomer(c); }}
                                            >
                                                {c.railtel_username}
                                            </span>
                                        </td>
                                        <td>{c.operator_name}</td>
                                        {hasExpiry && (
                                            <td title={c.expiry_synced_at ? `Last checked ${c.expiry_synced_at}` : "Not synced yet"}>
                                                {c.expiry_date ? <ExpiryDate value={c.expiry_date} /> : "—"}
                                            </td>
                                        )}
                                        <td>{c.linked_at}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {total > 0 && (
                            <Pagination page={page} perPage={perPage} total={total} loading={loading}
                                onPage={goPage} onPerPage={changePerPage} />
                        )}
                    </div>
                )}

            </div>
        </>
    );
};

export default RailtelCustomersList;
